import _ from 'underscore';
import reactive = require('../reactive');

import Grid from "./grid";
import {Questionary, QuestionaryVariable} from "../questionary";
import {XmlTagInfo} from "../xmlTagProvider";
import "./../groups/syntheticGroup";
import "./../groups/radioGroup";
import "./../groups/comboGroup";
import "./../groups/checkboxGroup";
import "./../groups/dragGroup";
import {FormItem} from "./formItem";
import FormGroup from "./formGroup";
import Variable from "../variable";
import {ValidateResult} from "../contentType";
import {CheckboxItem} from "../items/checkbox";
import Cell from "../form/cell";
import {XmlUtils} from "../utils";
import {ExpressionParser, Event, Filter} from "../tags/expression";
import {CellItem} from "../items/cellItem";
import EventEmitter from 'wolfy87-eventemitter';
import ICustomForm, {ValidationError} from "../../customForms/ICustomForm";

export class Form extends EventEmitter implements FormItem {

	parent?: FormGroup;

	/**
	 * Name of class to distinguish between Form and FormGroup
	 */
	className: string = 'Form';

	timeElapsed?: QuestionaryVariable<number>;

	timer?: number;
	backup?: { [name:string]: any };

	validateResult: ValidateResult = ValidateResult.okResult();
	activeGlobalUnknown: CheckboxItem|null = null;

    /**
     * Css class given by xml definition
     */
	class: string = '';

	nextFormId?: string;  // Can be redefined by randomization
	grid: Grid;

	customForm: ICustomForm|null = null;

    aux1?: string;
    aux2?: string;
    aux3?: string;

    private constructor(
        public id: string,
        public valuesCheck: boolean,
        public number: string,
        public backButtonVisible: boolean,
        public nextButtonVisible: boolean,
        public text: string,
        public hint: string,
        public variables: _.Dictionary<Variable>,
        cssClass: string,
	    private _questionary: Questionary,
        private preFilters: Filter[],
        private postFilters: Filter[],
        private events: Event[],
        grid: Grid,
        nextFormId?: string,
    ) {
	    super();
        this.class = cssClass;
        this.nextFormId = nextFormId;
        this.grid = grid;

        if (this._questionary) {
            // time variable is not tracked by auto redraw mechanism, it should not be used directly in view
            this.timeElapsed = this._questionary.createVariable(this.timeVariableName, 'number', true, undefined);
            this.timeElapsed(0);
        }
    }

	static fromXML(node: XmlTagInfo): Form {
        const children = _.indexBy(<XmlTagInfo[]>node.children, 'tag');
        const questionary = node.scope!.questionary;
		const form: Form = new Form(
            node.attributes['id'],
            node.attributes['valuesCheck'] === "yes",
            node.attributes['number'],
            node.attributes['backButtonVisible'] !== "no",  // backwards compatibility - not check to 'yes'
            node.attributes['nextButtonVisible'] !== "no",  // backwards compatibility - not check to 'yes'
            children['text'].value || "",
            children['hint'].value || "",
            _.indexBy(children['variableList'].children, 'name'),
            node.attributes['class'] ? node.attributes['class'] : '',
            node.scope!.questionary,
            XmlUtils.childByName(children['filterList'], 'preFilter').children.map(n => ExpressionParser.parseFilter(n)),
            XmlUtils.childByName(children['filterList'], 'postFilter').children.map(n => ExpressionParser.parseFilter(n)),
            children['eventList'].children.map((n:any) => ExpressionParser.parseEvent(n)),
            // @ts-ignore Grid needs form and form needs grid to construct
			undefined,
            node.attributes['nextForm']
        );

		form.aux1 = children['aux1']?.value;
		form.aux2 = children['aux2']?.value;
		form.aux3 = children['aux3']?.value;

		// Setup grid
        form.grid = Grid.fromXML(children['model'].children[0], questionary, form);

        // Add form to questionary
        questionary.formsById[form.id] = form;

        if (form.questionary) {
            // v.type expect to be defined from xml
            _.each(form.variables, (v: Variable) => form.questionary.createVariable(v.name, v.type!, false, undefined));
        }

		return form;
	}

	// for testing purposes
	static createEmpty(id: string, number: string, questionary: Questionary, next?: string, ): Form {
        return new Form(
            id, false, number,
            false, false, '',
            '', {}, '',
            questionary, [], [],
			[], <Grid>{}, next);
    }

	get questionary(): Questionary {
		return this._questionary!;
	}

	get timeVariableName(): string {
		return this.id + '_time';
	}

	get firstId() {
		return this.id;
	}

	startTiming (){
		const elapsed = this.timeElapsed!;
		const start = new Date().getTime() - (elapsed() || 0);

		this.timer = window.setInterval(() => {
			elapsed(new Date().getTime() - start);
		}, 50); // some older browsers (IE) have getTime precision about 15ms, 50ms should be enough for answer timing.

	}

	stopTiming(): void {
		clearInterval(this.timer);
	}

	// check timer - if preload is finished after prefilters, it can start unwanted second timer on inactive form
	checkTiming(): void {
        if (this !== this.questionary.currentForm) {
            this.stopTiming();
        }
    }

	getNumber(): number {
		if (this.class.startsWith('customForm-Page-zero')) {
			return 0;
		}
		let shift = 0;
		this.questionary.getFormList().every(f => f.class.startsWith('customForm-Page-zero') ? ++shift : false);
		return parseInt(this.questionary.state.random.formNumbers[this.id!] || this.number) - shift;
	}

	backupAndClear (){
		let backup:{ [name:string]: any } = {};
		let variables: QuestionaryVariable<any>[] = [];

		// save variables state
		_.each(this.variables!, (v: Variable) => {
			const variable = this.questionary.variableByName(v.name);
			if (!variable.computed) {
				backup[v.name] = variable();
				variables.push(variable);
			}
		});

		// reset variables
		reactive.stopRedraw();
		_(variables).each((v:QuestionaryVariable<any>) => v(null));
		reactive.startRedraw();

		this.backup = backup;
	}

	groupById(id: string) {
		return this.grid.groupById(id);
	}

	/**
	 * Init groups after the whole questionary (all forms) is loaded
	 */
	init() {
		this.grid.init();
		this.randomizeGrid();
		this.grid.createLayout();
		this.bindEvents();
	}

	/**
	 * Init groups after the whole questionary is loaded
	 */
	initGroups() {
		// this.grid.initGroups();
	}

    /**
     * @return boolean Returns true for valid, false for invalid
     */
	validate() {
		if (!this.valuesCheck || this.activeGlobalUnknown) {
			this.grid.cells.forEach(c => c.valid = true);
			this.validateResult =  ValidateResult.okResult();
		} else {
			this.validateResult = this.grid.cells.reduce((acc, c: Cell) => acc.createMerged(c.validate()), ValidateResult.okResult());
			// const cells: Cell[] = _.unique(_.values(this.grid.cellById));
			// this.validateResult = _.values(this.grid.cellById).reduce((acc, c: Cell) => acc.createMerged(c.validate()), ValidateResult.okResult());
		}
		return this.validateResult.valid;
	}


    /**
     * Validate form and collect errors
     */
	collectValidationErrors(): ValidationError[] {
        const errors: ValidationError[] = [];

        if (!this.validate()) {
            // form has some errors, at least one
	        for (let cell of this.grid.cells) {
	            const cellResult = cell.validate();
	            if (!cellResult.valid) {
	                // cell with error
                    errors.push( {
                        messages: cellResult.messages,
                        target: cell
                    });
                }
            }
        }

	    return errors;
    }

	reValidate() {
		if(!this.validateResult.valid){
			this.validate();
		}
	}

	isEmpty() {
		let empty = true;
		if (this.grid) {
		    this.grid.eachControl((control: CellItem) => empty = empty && control.isEmpty() );
        }
		return empty;
	}

	reset () {
	    if (this.grid) {
		    this.grid.eachControl(control => control.reset())
        }
	}

	// Reset checkbox which is marked as globalUnknown
    resetGlobalUnknown () {
        if (this.grid) {
            this.grid.eachControl((control) => {
                if (control instanceof CheckboxItem && control.globalUnknown) {
                    control.reset();
                }
            })
        }
    }

	resourcesToPreload() {
		const resources: any[] = [];
		if (this.grid) {
		    this.grid.eachControl(control => resources.push(control.resourcesToPreload()));
        }

		return _.unique(_.flatten(resources));
	}

	onNext() {
	    if (this.grid) {
            // reset controls in hidden cells
            this.grid.cells.forEach((cell: Cell) => {
                _(cell.cellItems).each((c: CellItem) => {
                    if (!cell.visible) {
                        c.unset();
                    }
                    c.onNext();
                });
            });
        }

		this._questionary!.eventReporter!.reportFormAnswered({formId: this.id});
	}

	onNavigatedTo() {
		this.restore();
		if (this.grid) {
            this.grid.eachControl((control) => control.onNavigatedTo());
        }
	}

	blockNext() {
		var block = false;
		if (this.grid) {
		    this.grid.eachControl(control => block = block || control.blockNext());
        }
		return block;
	}

	isSimpleForm() {
		return false; // Feature is disabled
		// var isSimple = true;
		// // check if it can be displayed as simple form
		// // has exactly 2 columns, one is label only second is radio only
		// if (this.grid.layout[0].length !== 2) {
		// 	isSimple = false;
		// } else {
		// 	this.grid.layout.forEach(function (row) {
		// 		if (row.length !== 2) {
		// 			isSimple = false;
		// 		} else {
		// 			row.forEach(function (cell) {
		// 				if (!cell || cell.controls.length > 1) {
		// 					isSimple = false;
		// 				} else {
		// 					isSimple = isSimple && cell.controls.every(c => c instanceof LabelItem || c instanceof RadioItem);
		// 				}
		// 			});
		// 		}
		// 	})
		// }
        //
		// // var isSimpleRadioOneColumn = this.grid.layout.every(row => {
		// // 	if( row.length != 1) {return false;}
		// // 	var cell = row[0];
		// // 	return cell && cell.controls.length == 1 && cell.controls[0] instanceof RadioItem;
		// // });
		// return isSimple;// || isSimpleRadioOneColumn;
	}

	/**
	 * Check active PRE_FILTER
	 * @returns {Filter} Active filter or undefined
	 */
	getActivePreFilter(): Filter|undefined {
		return this.getActiveFilter(this.preFilters);
	}
	/**
	 * Check active POST_FILTER
	 * @returns {Filter} Active filter or undefined
	 */
	getActivePostFilter(): Filter|undefined {
		return this.getActiveFilter(this.postFilters);
	}

	/**
	 * Form is to be shown
	 */
	onShow() {
		this.processEvents();
	}

	private getActiveFilter(filters: Filter[]): Filter|undefined {
        return filters.filter((f) => {
            try {
                return f.condition.evaluate(this._questionary!.state);
            } catch (err) {
                console.error(err.message, err.stack);
                throw new Error(`Unable to evaluate filter "${f.describe()}" on form ID ${this.id}.`)
            }
        })[0];
	}

	private restore() {
		if (this.backup) {
			reactive.stopRedraw();
			_(this.backup).each((value: any, name: string) => this.questionary.variableByName(name)(value));
			reactive.startRedraw();
			delete this.backup;
		}
	}

	private bindEvents(): void {
		const variables = this._questionary.state.variables;
		this.events.forEach( (e: Event) => {
			reactive.bind( () => {
				if( this._questionary.currentForm === this ) {
					this.processEvents();
				}
			}, _.pick(variables, e.condition.dependencies));
		});
	}

	private processEvents(): boolean {
	    let submit = false;
	    if (this.grid) {
            submit = this.grid.processEvents(this.events);
            if (submit) {
                this._questionary.trigger(Questionary.Events.SUBMIT_FORM);
            }
            this._questionary.trigger(Questionary.Events.EVENTS_COMPUTED);
        }
		return submit;
	}

	private randomizeGrid(): void {
		// TODO state should be saved in Grid. Is it good idea?
        const permutation = this._questionary.state.random.form[this.id];
		if (this.grid) {
            const res = this.grid.applyRandomize(permutation);
            if (res) {
                this._questionary.state.random.form[this.id] = res;
            }
        }
	}

}
